import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Badge, Button, Modal, Form, Alert } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader.jsx';

export default function ApiKeysPage() {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [editingId, setEditingId] = useState(null);   // null = create-Modus, string = edit-Modus
  const [name, setName] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [created, setCreated] = useState(null);
  const [error, setError] = useState(null);

  const keys = useQuery({ queryKey: ['api-keys'], queryFn: () => api.get('/api-keys').then(r => r.data.items) });
  const permissions = useQuery({ queryKey: ['permissions'], queryFn: () => api.get('/roles/permissions').then(r => r.data.items) });

  // Permissions die für service-zu-service-Keys NICHT vergeben werden sollen.
  // Begründung: Benutzer-, Rollen- und Settings-Verwaltung sind Admin-Aktionen,
  // die über die UI durch echte Personen erfolgen sollen — nicht durch Automaten.
  const SERVICE_SAFE_RESOURCES = new Set([
    'customers', 'licenses', 'invoices', 'payments',
    'expenses', 'subscriptions', 'donations', 'features', 'audit',
    'finance', 'accounting', 'attachments', 'external_refs',
    'dunning',
    // Bestell-Wizard im public-frontend: core-service-Service-Account braucht
    // Lese-Rechte auf commitment_terms (Slider-Stops) und Validate-Recht auf
    // discount_codes (Pre-Flight-Check vor Order-Submit). payment_methods ist
    // ebenfalls Lookup-only fuer Rechnungs-Anzeige im Frontend.
    'commitment_terms', 'discount_codes', 'payment_methods'
  ]);
  // Innerhalb erlaubter Ressourcen sind manche Aktionen trotzdem rein admin
  // (z.B. dunning:manage_levels = Mahnstufen-Katalog editieren).
  const ADMIN_ONLY_ACTIONS = new Set([
    'dunning:manage_levels',
    'features:create', 'features:update',  // Katalog-Pflege
    // Billing-Konfig: nur Admins legen Codes/Stufen/Methoden an. S2S-Service-
    // Accounts brauchen davon nur read + redeem (Code-Validation).
    'discount_codes:create', 'discount_codes:update', 'discount_codes:delete',
    'commitment_terms:create', 'commitment_terms:update', 'commitment_terms:delete',
    'payment_methods:create', 'payment_methods:update', 'payment_methods:delete'
  ]);

  const grouped = useMemo(() => {
    const map = {};
    for (const p of permissions.data || []) {
      if (!SERVICE_SAFE_RESOURCES.has(p.resource)) continue;
      if (ADMIN_ONLY_ACTIONS.has(`${p.resource}:${p.action}`)) continue;
      (map[p.resource] ||= []).push(p);
    }
    return map;
  }, [permissions.data]);

  const save = useMutation({
    mutationFn: (body) => editingId
      ? api.patch(`/api-keys/${editingId}`, body).then(r => r.data)
      : api.post('/api-keys', body).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      setCreated(data);                  // zeigt den neuen Klartext-Key (auch beim Update — Key wird rotiert)
    },
    onError: (e) => setError(e.response?.data?.message || JSON.stringify(e.response?.data))
  });

  const revoke = useMutation({
    mutationFn: (id) => api.post(`/api-keys/${id}/revoke`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] })
  });

  const openCreate = () => {
    setEditingId(null);
    setName('');
    setSelected(new Set());
    setCreated(null); setError(null); setShow(true);
  };
  const openEdit = (k) => {
    setEditingId(k.id);
    setName(k.name);
    setSelected(new Set(k.permissions));
    setCreated(null); setError(null); setShow(true);
  };
  const close = () => { setShow(false); setCreated(null); setError(null); setEditingId(null); };

  return (
    <>
      <PageHeader title="API-Keys" subtitle="Service-zu-Service-Tokens für die Kontor-API"
        actions={<Button onClick={openCreate}><Icon icon="tabler:plus" className="me-1" />Neuer API-Key</Button>} />

      <Card className="card-flat">
        <Card.Body className="p-0">
          <Table hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr><th>Name</th><th>Prefix</th><th>Permissions</th><th>Status</th><th>Zuletzt verwendet</th><th></th></tr>
            </thead>
            <tbody>
              {(keys.data || []).map(k => (
                <tr key={k.id}>
                  <td className="fw-semibold">{k.name}</td>
                  <td><code className="small">{k.key_prefix}…</code></td>
                  <td>
                    {k.permissions.length > 4
                      ? <span className="text-muted">{k.permissions.length} Permissions</span>
                      : k.permissions.map(p => <code key={p} className="small me-2">{p}</code>)}
                  </td>
                  <td>{k.revoked_at
                    ? <Badge bg="" className="badge-soft-danger">widerrufen</Badge>
                    : <Badge bg="" className="badge-soft-success">aktiv</Badge>}</td>
                  <td className="text-muted small">{k.last_used_at ? new Date(k.last_used_at).toLocaleString('de-DE') : 'nie'}</td>
                  <td className="text-end pe-3">
                    {!k.revoked_at && (
                      <>
                        <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => openEdit(k)} title="Permissions ändern (rotiert den Key)">
                          <Icon icon="tabler:edit" className="me-1" />Bearbeiten
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => {
                          if (confirm(`Key "${k.name}" widerrufen? Aktive Service-Connections werden sofort 401.`)) revoke.mutate(k.id);
                        }}>Widerrufen</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={show} onHide={close} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {created ? (editingId ? 'API-Key aktualisiert (neuer Token)' : 'API-Key erstellt')
                     : (editingId ? `API-Key bearbeiten: ${name}` : 'Neuer API-Key')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
          {created ? (
            <>
              <Alert variant="warning"><Icon icon="tabler:alert-triangle" className="me-2" />
                Beim Bearbeiten wird <strong>immer ein neuer Klartext-Token</strong> erzeugt
                und der alte überschrieben. Speichere ihn sicher — er wird nur jetzt einmalig angezeigt.
              </Alert>
              <textarea className="form-control font-monospace small" rows={3} readOnly
                value={created.plaintext_key}
                onClick={(e) => e.target.select()}
                style={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap', resize: 'vertical' }} />
              <Button className="mt-2" onClick={() => navigator.clipboard.writeText(created.plaintext_key)}>
                <Icon icon="tabler:copy" className="me-1" />Kopieren
              </Button>
            </>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control value={name} onChange={(e) => setName(e.target.value)} placeholder="z.B. core-service prod" />
              </Form.Group>
              <Form.Group>
                <Form.Label>Permissions *</Form.Label>
                <Form.Text className="d-block mb-2">
                  Nur service-relevante Berechtigungen sind hier wählbar. Benutzer-,
                  Rollen- und Settings-Verwaltung bleiben Admin-UI-only.
                </Form.Text>
                {Object.entries(grouped).map(([resource, items]) => (
                  <div key={resource} className="mb-2">
                    <div className="fw-semibold mb-1">{resource}</div>
                    <div className="d-flex flex-wrap gap-2">
                      {items.map(p => (
                        <Form.Check key={p.id} type="checkbox" id={`p-${p.id}`}
                          label={<span><code className="small">{p.scope}:{p.action}</code> <span className="text-muted">— {p.label}</span></span>}
                          checked={selected.has(p.permission_string)}
                          onChange={(e) => {
                            const s = new Set(selected);
                            if (e.target.checked) s.add(p.permission_string); else s.delete(p.permission_string);
                            setSelected(s);
                          }} />
                      ))}
                    </div>
                  </div>
                ))}
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={close}>{created ? 'Schließen' : 'Abbrechen'}</Button>
          {!created && (
            <Button onClick={() => save.mutate({ name, permissions: Array.from(selected) })}
              disabled={!name || selected.size === 0 || save.isLoading}>
              {save.isLoading ? 'Speichert…' : (editingId ? 'Speichern (Key wird neu erzeugt)' : 'Erstellen')}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}
